import React, { useState, useEffect, useCallback } from 'react';
import * as policyApi from '../services/policyApi';

const CATEGORY_META = {
  federal: { label: 'Federal Policy', icon: '🏛️' },
  state: { label: 'State Policy', icon: '🗺️' },
  ceo_directives: { label: 'CEO Directives', icon: '👔' },
  preferences: { label: 'Preferences', icon: '⚙️' },
};

const CATEGORY_ORDER = ['federal', 'state', 'ceo_directives', 'preferences'];

function ReassemblyStatusBadge({ status }) {
  const map = {
    success: { bg: '#E8F5E9', color: '#2E7D32', label: '✅ Assembled' },
    running: { bg: '#FFF3E0', color: '#E65100', label: '⏳ Assembling…' },
    failed: { bg: '#FFEBEE', color: '#C62828', label: '❌ Assembly Failed' },
    unknown: { bg: '#F5F5F5', color: '#757575', label: '— Unknown' },
  };
  const s = map[status] || map.unknown;
  return (
    <span style={{
      background: s.bg,
      color: s.color,
      padding: '4px 14px',
      borderRadius: '14px',
      fontSize: '12px',
      fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
}

function SourceBadge({ source }) {
  const isSelf = source === 'self' || source === 'own';
  return (
    <span style={{
      background: isSelf ? '#E3F2FD' : '#F3E5F5',
      color: isSelf ? '#1565C0' : '#7B1FA2',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 600,
    }}>
      {isSelf ? 'Own' : 'Inherited from parent'}
    </span>
  );
}

function PreferencesFields({ structured }) {
  if (!structured) return null;
  const fields = [
    { key: 'locations', label: 'Locations' },
    { key: 'language', label: 'Language' },
    { key: 'timings', label: 'Timings' },
    { key: 'ddl_instructions', label: 'DDL Instructions' },
  ];
  return (
    <div style={{ marginTop: '10px', background: '#F8F9FA', borderRadius: '6px', padding: '10px 14px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '8px' }}>Structured Preferences</div>
      {fields.map(({ key, label }) => {
        const val = structured[key];
        if (!val) return null;
        return (
          <div key={key} style={{ marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#333' }}>{label}: </span>
            <span style={{ fontSize: '11px', color: '#555' }}>
              {Array.isArray(val) ? val.join(', ') : String(val)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * ConsolidatedGovernanceView
 *
 * Props:
 *   clientId    — string
 *   companyName — string
 *   userRole    — string
 */
const ConsolidatedGovernanceView = ({ clientId, companyName, userRole }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [conflictsExpanded, setConflictsExpanded] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const fetchConsolidated = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await policyApi.getConsolidated(clientId);
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load consolidated governance.');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchConsolidated();
  }, [fetchConsolidated]);

  // Listen for policy-reassembled event to auto-refresh
  useEffect(() => {
    const handler = () => fetchConsolidated();
    window.addEventListener('policy-reassembled', handler);
    return () => window.removeEventListener('policy-reassembled', handler);
  }, [fetchConsolidated]);

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('pnp_access_token') || localStorage.getItem('pnp_id_token') || '';
      const blob = await policyApi.downloadConsolidatedPdf(clientId, token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(companyName || 'governance').replace(/\s+/g, '-')}_consolidated_policy.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`PDF download failed: ${err.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const toggleCategory = (cat) => {
    setExpanded((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  if (loading) {
    return (
      <div style={{ padding: '20px 0', color: '#888', fontSize: '13px' }}>
        ⏳ Loading consolidated governance…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: '8px', padding: '12px 16px', color: '#C62828', fontSize: '13px', marginTop: '16px' }}>
        ⚠️ {error}
      </div>
    );
  }

  // Empty state
  if (!data || !data.categories || Object.keys(data.categories).length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px', color: '#888', marginTop: '16px' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>📜</div>
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px' }}>No active policies</div>
        <div style={{ fontSize: '13px', marginBottom: '16px' }}>
          No governance documents have been assembled yet.
        </div>
        {userRole === 'ceo_authority' && (
          <a
            href="#policies"
            style={{ color: '#1565C0', fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}
          >
            Manage Policies →
          </a>
        )}
      </div>
    );
  }

  const categories = data.categories || {};
  const reassemblyStatus = data.reassembly_status || data.status || 'unknown';
  const governanceContext = data.governance_context || {};
  const conflicts = data.conflicts || [];

  return (
    <div style={{ marginTop: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h4 style={{ margin: 0, color: '#1a1a2e' }}>📜 Policy Governance</h4>
          <ReassemblyStatusBadge status={reassemblyStatus} />
        </div>
        <button
          onClick={handleDownloadPdf}
          disabled={downloadingPdf}
          style={{
            padding: '8px 16px',
            background: downloadingPdf ? '#ccc' : '#1565C0',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: downloadingPdf ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          {downloadingPdf ? '⏳ Downloading…' : '📄 Download PDF'}
        </button>
      </div>

      {/* Per-category collapsible panels */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
        {CATEGORY_ORDER.map((cat) => {
          const policy = categories[cat];
          if (!policy) return null;
          const meta = CATEGORY_META[cat] || { label: cat, icon: '📄' };
          const isExpanded = expanded[cat];

          return (
            <div key={cat} style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                onClick={() => toggleCategory(cat)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  background: isExpanded ? '#E3F2FD' : '#f8f9fa',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1a1a2e',
                  textAlign: 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span>{meta.icon} {meta.label}</span>
                  <SourceBadge source={policy.source} />
                  {policy.version && (
                    <span style={{ fontSize: '11px', color: '#888', fontWeight: 400 }}>v{policy.version}</span>
                  )}
                </div>
                <span style={{ color: '#888', fontSize: '16px' }}>{isExpanded ? '▲' : '▼'}</span>
              </button>

              {isExpanded && (
                <div style={{ padding: '14px 16px', background: '#fff' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Version</div>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>v{policy.version || '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Effective Date</div>
                      <div style={{ fontSize: '13px' }}>{policy.effective_date ? policy.effective_date.slice(0, 10) : '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>Uploaded By</div>
                      <div style={{ fontSize: '13px' }}>{policy.uploader || policy.uploaded_by || '—'}</div>
                    </div>
                  </div>

                  {/* Preferences structured fields */}
                  {cat === 'preferences' && policy.structured && (
                    <PreferencesFields structured={policy.structured} />
                  )}

                  {/* Directives text */}
                  {policy.directives_text && (
                    <div style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>Directives</div>
                      <div style={{ background: '#f8f9fa', borderRadius: '6px', padding: '10px 14px', fontSize: '12px', color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap', maxHeight: '200px', overflow: 'auto' }}>
                        {policy.directives_text}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Resolved directives panel */}
      {governanceContext.directives_text && (
        <div style={{ background: '#F8F9FA', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '16px', marginBottom: '16px' }}>
          <h5 style={{ margin: '0 0 10px', color: '#1a1a2e' }}>📋 Resolved Directives</h5>
          <div style={{ fontSize: '12px', color: '#333', lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>
            {governanceContext.directives_text}
          </div>
        </div>
      )}

      {/* Conflicts section */}
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
        <button
          onClick={() => setConflictsExpanded((v) => !v)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: conflictsExpanded ? '#FFF3E0' : '#f8f9fa',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            color: '#1a1a2e',
            textAlign: 'left',
          }}
        >
          <span>
            ⚠️ Conflicts
            <span style={{
              marginLeft: '8px',
              background: conflicts.length > 0 ? '#FF9800' : '#E0E0E0',
              color: conflicts.length > 0 ? '#fff' : '#888',
              padding: '1px 8px',
              borderRadius: '10px',
              fontSize: '11px',
            }}>
              {conflicts.length}
            </span>
          </span>
          <span style={{ color: '#888', fontSize: '16px' }}>{conflictsExpanded ? '▲' : '▼'}</span>
        </button>

        {conflictsExpanded && (
          <div style={{ padding: '12px 16px', background: '#fff' }}>
            {conflicts.length === 0 ? (
              <div style={{ color: '#888', fontSize: '12px' }}>No conflicts detected.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {conflicts.map((conflict, idx) => (
                  <div key={idx} style={{ background: '#FFF8E1', borderRadius: '6px', padding: '10px 14px', border: '1px solid #FFE082', fontSize: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '1px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}>
                        {conflict.winner_category || conflict.winner}
                      </span>
                      <span style={{ color: '#888' }}>overrides</span>
                      <span style={{ background: '#FFEBEE', color: '#C62828', padding: '1px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}>
                        {conflict.loser_category || conflict.loser}
                      </span>
                    </div>
                    {conflict.directive && (
                      <div style={{ color: '#555', fontStyle: 'italic' }}>
                        "{conflict.directive.length > 100 ? `${conflict.directive.slice(0, 100)}…` : conflict.directive}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsolidatedGovernanceView;
